import { Download, File } from "lucide-react";
import { useState } from "react";
import { useSandboxFile } from "@/hooks/agent/useSandboxFile";
import { cn } from "@/lib/utils";

interface FileDownloadButtonsProps {
  content: string;
  className?: string;
}

// ファイルタイプの表示名マッピング
const FILE_TYPE_LABELS: Record<string, string> = {
  html: "HTML",
  md: "Markdown",
  xlsx: "Excel",
  docx: "Word",
  pdf: "PDF",
  pptx: "PowerPoint",
  png: "PNG Image",
  jpg: "JPG Image",
  jpeg: "JPEG Image",
  gif: "GIF Image",
  svg: "SVG Image",
  csv: "CSV",
  tsv: "TSV",
  zip: "ZIP Archive",
  txt: "Text",
};

// ダウンロードボタンを表示する拡張子のリスト
const DOWNLOADABLE_EXTENSIONS = Object.keys(FILE_TYPE_LABELS);

// ファイルタイプごとの色マッピング
const FILE_TYPE_COLORS: Record<string, { bg: string; icon: string; }> = {
  xlsx: { bg: "#e6f4ea", icon: "#34a853" }, // 緑 (Excel)
  csv: { bg: "#e6f4ea", icon: "#34a853" }, // 緑 (データ)
  tsv: { bg: "#e6f4ea", icon: "#34a853" }, // 緑 (データ)
  docx: { bg: "#e8f0fe", icon: "#4285f4" }, // 青 (Word)
  pptx: { bg: "#fef3e8", icon: "#f57c00" }, // オレンジ (PowerPoint)
  pdf: { bg: "#fce8e6", icon: "#d93025" }, // 赤 (PDF)
  png: { bg: "#fef9e7", icon: "#f9a825" }, // 黄色系 (PNG)
  jpg: { bg: "#fff3e0", icon: "#ff6f00" }, // 黄色〜赤系 (JPG)
  jpeg: { bg: "#fff3e0", icon: "#ff6f00" }, // 黄色〜赤系 (JPEG)
  gif: { bg: "#fef3e8", icon: "#e65100" }, // 赤系〜オレンジ系 (GIF)
  svg: { bg: "#f3e8fd", icon: "#9334e9" }, // 紫 (SVG)
  html: { bg: "#e1f5fe", icon: "#29b6f6" }, // 水色系 (HTML)
  md: { bg: "#eceff1", icon: "#607d8b" }, // 灰色〜水色系 (Markdown)
  txt: { bg: "#f1f3f4", icon: "#5f6368" }, // グレー (テキスト)
  zip: { bg: "#f1f3f4", icon: "#5f6368" }, // グレー (アーカイブ)
};

/**
 * /workspace/で始まるファイルパスを抽出
 */
const extractWorkspaceFiles = (content: string): string[] => {
  const paths: string[] = [];
  const regex = /\/workspace\/[^\s"`'\n)]+/g;
  const matches = content.match(regex);

  if (!matches) return [];

  for (const match of matches) {
    // 拡張子をチェック
    const extension = match.split(".").pop()?.toLowerCase();
    if (extension && DOWNLOADABLE_EXTENSIONS.includes(extension)) {
      // 重複を避ける
      if (!paths.includes(match)) {
        paths.push(match);
      }
    }
  }

  return paths;
};

/**
 * ファイル名を取得
 */
const getFileName = (filePath: string): string => {
  return filePath.split("/").pop() || filePath;
};

/**
 * ファイルタイプラベルを取得
 */
const getFileTypeLabel = (filePath: string): string => {
  const extension = filePath.split(".").pop()?.toLowerCase();
  if (extension && FILE_TYPE_LABELS[extension]) {
    return FILE_TYPE_LABELS[extension];
  }
  return extension?.toUpperCase() || "File";
};

/**
 * ファイルタイプの色を取得
 */
const getFileTypeColor = (filePath: string): { bg: string; icon: string; } => {
  const extension = filePath.split(".").pop()?.toLowerCase();
  if (extension && FILE_TYPE_COLORS[extension]) {
    return FILE_TYPE_COLORS[extension];
  }
  return { bg: "#dbeafe", icon: "#6888E1" };
};

const FileDownloadButtons: React.FC<FileDownloadButtonsProps> = ({
  content,
  className,
}) => {
  const { downloadFile, isDownloadingFile } = useSandboxFile();
  const files = extractWorkspaceFiles(content);
  const [hoveredFile, setHoveredFile] = useState<string | null>(null);

  if (files.length === 0) return null;

  const handleDownload = async (filePath: string) => {
    try {
      await downloadFile(filePath);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <div className={cn("flex flex-wrap gap-2 mt-4", className)}>
      {files.map((filePath) => {
        const fileName = getFileName(filePath);
        const fileType = getFileTypeLabel(filePath);
        const fileColor = getFileTypeColor(filePath);
        const isDownloading = isDownloadingFile(filePath);
        const isHovered = hoveredFile === filePath;

        return (
          <button
            key={filePath}
            onClick={() => handleDownload(filePath)}
            disabled={isDownloading}
            title={filePath}
            onMouseEnter={() => setHoveredFile(filePath)}
            onMouseLeave={() => setHoveredFile(null)}
            className={cn(
              "flex items-center px-[11px] py-[9px] rounded-[10px] border transition-colors",
              "w-[240px]",
              "bg-white border-[#e5e7eb]",
              "hover:bg-[#f9fafb] cursor-pointer",
              "disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            {/* アイコン */}
            <div
              className="flex-shrink-0 w-8 h-8 mr-2 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: fileColor.bg }}
            >
              <File className="w-[18px] h-[18px]" style={{ color: fileColor.icon }} />
            </div>

            {/* ファイル情報 */}
            <div className="flex-1 text-left min-w-0 text-xs mr-1.5">
              <div className="font-medium text-[#474747] truncate" title={fileName}>
                {fileName}
              </div>
              <div className="text-xs text-[#9ca3af] flex items-center gap-1">
                <span>{fileType}</span>
              </div>
            </div>

            {/* ダウンロードアイコン */}
            <div className="flex-shrink-0">
              {isDownloading
                ? (
                  <div
                    className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: fileColor.icon, borderTopColor: "transparent" }}
                  />
                )
                : (
                  <Download
                    className="w-4 h-4 transition-colors"
                    style={{ color: isHovered ? fileColor.icon : "#9ca3af" }}
                  />
                )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default FileDownloadButtons;
